import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCategoryParam } from '../../hooks/useCategoryParam';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { Button } from '../../components/ui/button';
import AddressSelector from '../../components/customer/AddressSelector';
import { toast } from 'sonner';
import {
  Search, Plus, Minus, ChevronDown, ChevronRight, Clock, User,
  Home, Grid3X3, ShoppingCart, Zap, Gift, Percent,
  ShoppingBag, Sun, Headphones, Sparkles, Leaf, Package, CalendarCheck
} from 'lucide-react';
import axios from 'axios';
import { PromoBannerCarousel } from '../../components/customer/PromoBannerCarousel';
import { resolveHomeBanner, themeHomeBannerDefaults } from '../../utils/homeBanner';
import { CouponStrip } from '../../components/customer/CouponStrip';
import { PromotionStrip } from '../../components/customer/PromotionStrip';
import { usePromotions } from '../../hooks/usePromotions';
import { UnavailableBadge, isUnavailable, isVariantUnavailable } from '../../components/customer/UnavailableBadge';
import { ComingSoonButton } from '../../components/customer/ComingSoonButton';
import { isComingSoon } from '../../utils/comingSoon';
import { ProductImageCarousel } from '../../components/customer/ProductImageCarousel';
import { SubCategoryFilter } from '../../components/customer/SubCategoryFilter';
import { VariantPicker } from '../../components/customer/VariantPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEARCH_HINTS = [
  '"atta"', '"dal"', '"sugar"', '"oil"', '"chips"', '"biscuits"',
];

const ZeptoRotatingSearch = () => {
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const timer = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setIdx(prev => (prev + 1) % SEARCH_HINTS.length);
        setFade(true);
      }, 200);
    }, 2200);
    return () => clearInterval(timer);
  }, []);

  return (
    <span className="text-gray-400 text-sm pointer-events-none transition-all duration-200"
      style={{ opacity: fade ? 1 : 0, transform: fade ? 'translateY(0)' : 'translateY(-6px)' }}>
      Search for {SEARCH_HINTS[idx]}
    </span>
  );
};

const ZeptoHome = () => {
  const { user, storeSettings } = useAuth();
  const { addItem, getItemQuantity, updateQuantity, totalItems, totalAmount } = useCart();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  // In the URL, not local state, so Back from a product returns to this tab.
  const [selectedTab, setSelectedTab] = useCategoryParam();
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [promoBanners, setPromoBanners] = useState([]);
  const saleBanner = resolveHomeBanner(storeSettings?.home_banner, themeHomeBannerDefaults('theme-zepto'));
  const [coupons, setCoupons] = useState([]);
  const promotions = usePromotions();
  const [selectedDeliveryAddress, setSelectedDeliveryAddress] = useState(null);
  // Serviceable Apartments: once the tenant uses the apartment-first address
  // flow (feature on + at least one apartment configured — same gating as
  // MapAddressPicker/DeliveryOrders/AdminOrders), delivery is always to one
  // known apartment/flat rather than a choice between several saved
  // addresses, so the generic multi-address switcher doesn't apply here.
  const isApartmentsEnabled = storeSettings?.tenant_features?.serviceable_apartments === true;
  const [hasApartments, setHasApartments] = useState(false);
  useEffect(() => {
    if (!isApartmentsEnabled) return;
    axios.get(`${API}/admin/serviceable-apartments`)
      .then(res => setHasApartments((res.data || []).length > 0))
      .catch(() => {});
  }, [isApartmentsEnabled]);
  const apartmentFlowActive = isApartmentsEnabled && hasApartments;
  const searchRef = useRef(null);

  const sessionId = useRef(
    sessionStorage.getItem('_sid') ||
    (() => { const s = Math.random().toString(36).slice(2); sessionStorage.setItem('_sid', s); return s; })()
  );

  useEffect(() => { loadData(); trackPageview(); }, [storeSettings?.admin_id]);

  const loadData = async () => {
    try {
      const adminId = user?.admin_id || storeSettings?.admin_id;
      let reqs;
      if (user && user.admin_id) {
        reqs = [
          axios.get(`${API}/admin/categories`),
          axios.get(`${API}/customer/products`),
        ];
        if (user.admin_id) {
          reqs.push(axios.get(`${API}/store/${user.admin_id}/promo-banners`).catch(() => ({ data: [] })));
          reqs.push(axios.get(`${API}/store/${user.admin_id}/coupons`).catch(() => ({ data: [] })));
        }
      } else if (adminId) {
        reqs = [
          axios.get(`${API}/public/store/${adminId}/categories`).catch(() => ({ data: [] })),
          axios.get(`${API}/public/store/${adminId}/products`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${adminId}/promo-banners`).catch(() => ({ data: [] })),
          axios.get(`${API}/store/${adminId}/coupons`).catch(() => ({ data: [] })),
        ];
      } else {
        setLoading(false);
        return;
      }
      const results = await Promise.all(reqs);
      setCategories(results[0].data);
      setProducts(results[1].data);
      if (results[2]) setPromoBanners(results[2].data);
      if (results[3]) setCoupons(results[3].data);
    } catch (e) {
      console.error('Load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const trackPageview = () => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/pageview`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, page: '/home', source: 'direct',
    }).catch(() => {});
  };

  const trackEvent = (type, extra = {}) => {
    if (!user?.admin_id) return;
    axios.post(`${API}/track/event`, {
      admin_id: user.admin_id, customer_id: user.id,
      session_id: sessionId.current, event_type: type, ...extra,
    }).catch(() => {});
  };

  const handleAdd = (product, selectedVariant = null) => {
    if (!user) {
      navigate('/login');
      toast.info('Please login to add items to cart');
      return;
    }
    addItem(product, 1, selectedVariant);
    const label = selectedVariant ? `${product.name} (${selectedVariant.label})` : product.name;
    toast.success(`Added ${label}`);
    trackEvent('add_to_cart', { product_id: product.id, product_name: product.name, variant: selectedVariant?.label });
  };

  const handleSelectTab = (tabId) => {
    setSelectedTab(tabId);
    setSelectedSubCategories([]);
  };

  const currencySymbol = storeSettings?.currency_symbol || '₹';
  const autoPlay = storeSettings?.auto_image_carousel !== false;
  const detailEnabled = storeSettings?.product_detail_page !== false;
  const filteredProducts = (selectedTab
    ? products.filter(p => {
        if (p.category_id !== selectedTab) return false;
        if (selectedSubCategories.length > 0 && !selectedSubCategories.includes(p.sub_category_id)) return false;
        return true;
      })
    : products
  );
  const searchedProducts = searchQuery.trim()
    ? products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  const dealProducts = products.filter(p => p.mrp > p.price).slice(0, 15);
  const allProducts = products.slice(0, 20);

  // Category icons mapping (fallback)
  const catIcons = [ShoppingBag, Sun, Headphones, Sparkles, Leaf, Gift, Zap, Grid3X3];

  return (
    <div className="min-h-screen bg-[var(--theme-background)] pb-24" data-testid="zepto-home">
      {/* ===== HEADER with purple gradient ===== */}
      <div className="zepto-header-gradient sticky top-0 z-50" data-testid="zepto-header">
        <div className="max-w-3xl mx-auto px-4 pt-3 pb-2">
          {/* Delivery info row */}
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="flex items-center gap-1.5">
                <Zap className="w-4 h-4 text-[var(--theme-primary)]" />
                <span className="font-heading font-bold text-lg text-[var(--theme-text-main)]">
                  {storeSettings?.store_name || '11 minutes'}
                </span>
              </div>
              {!apartmentFlowActive && (
                <div className="mt-0.5 max-w-[200px]" data-testid="zepto-address">
                  <AddressSelector
                    selectedAddress={selectedDeliveryAddress}
                    onSelect={setSelectedDeliveryAddress}
                  />
                </div>
              )}
            </div>
            <Link to="/profile" className="w-10 h-10 rounded-full bg-[var(--theme-primary)] flex items-center justify-center" data-testid="zepto-profile-icon">
              <User className="w-5 h-5 text-white" />
            </Link>
          </div>

          {/* Quick access pills */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2" data-testid="zepto-quick-pills">
            <div className="shrink-0 bg-[var(--theme-primary)] text-white px-4 py-1.5 rounded-full text-xs font-bold">
              {storeSettings?.store_name || 'zepto'}
            </div>
            <Link to="/products" className="shrink-0 bg-white border border-[var(--theme-border)] px-3 py-1.5 rounded-full text-xs font-bold text-[var(--theme-primary)]">
              Best Deals
            </Link>
            <Link to="/products" className="shrink-0 bg-white border border-[var(--theme-border)] px-3 py-1.5 rounded-full text-xs font-bold text-[var(--theme-text-main)]">
              All Products
            </Link>
            <Link to="/subscriptions" className="shrink-0 bg-white border border-[var(--theme-border)] px-3 py-1.5 rounded-full text-xs font-bold text-green-700 flex items-center gap-1">
              <Leaf className="w-3 h-3" /> Fresh
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* ===== SEARCH BAR ===== */}
        <div className="px-4 pt-3 pb-2" data-testid="zepto-search-section">
          <div className={`bg-white border rounded-xl px-4 py-3 flex items-center gap-3 shadow-sm transition-all ${searchFocused ? 'border-[var(--theme-primary)] ring-2 ring-[var(--theme-primary)]/15' : 'border-[var(--theme-border)]'}`}
            onClick={() => searchRef.current?.focus()}>
            <Search className="w-5 h-5 text-gray-400 shrink-0" />
            <div className="flex-1 relative">
              {/* The input stays in normal flow so it always defines this wrapper's
                  height; the hint overlays it. With the input absolutely positioned
                  instead, the wrapper collapsed to 0px the moment the hint unmounted
                  (on focus/typing) and the typed text became invisible. */}
              {!searchFocused && !searchQuery && (
                <div className="absolute inset-0 flex items-center pointer-events-none">
                  <ZeptoRotatingSearch />
                </div>
              )}
              <input ref={searchRef} value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)} onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                className="w-full bg-transparent outline-none text-sm text-[var(--theme-text-main)]"
                data-testid="zepto-search-input" />
            </div>
          </div>
        </div>

        {/* ===== SEARCH RESULTS ===== */}
        {searchedProducts && (
          <div className="px-4 pb-4" data-testid="zepto-search-results">
            <p className="text-xs text-[var(--theme-text-muted)] mb-2">
              {searchedProducts.length} result{searchedProducts.length !== 1 ? 's' : ''} for "{searchQuery}"
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
              {searchedProducts.slice(0, 8).map(p => (
                <ZeptoProductGrid key={p.id} product={p} curr={currencySymbol}
                  getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                  onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
              ))}
            </div>
            {searchedProducts.length === 0 && <p className="text-center py-8 text-[var(--theme-text-muted)] text-sm">No products found</p>}
          </div>
        )}

        {/* ===== MAIN CONTENT ===== */}
        {!searchedProducts && (
          <>
            {/* Category Tabs with icons */}
            <div className="flex gap-4 overflow-x-auto scrollbar-hide px-4 pb-3 pt-1" data-testid="zepto-category-tabs">
              <button onClick={() => handleSelectTab(null)}
                className="flex flex-col items-center gap-1 shrink-0 min-w-[56px]"
                data-testid="zepto-tab-all">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${!selectedTab ? 'bg-[var(--theme-primary)] text-white' : 'bg-[var(--theme-secondary)] text-[var(--theme-primary)]'}`}>
                  <ShoppingBag className="w-5 h-5" />
                </div>
                <span className={`text-[10px] font-medium ${!selectedTab ? 'text-[var(--theme-primary)] font-bold' : 'text-[var(--theme-text-muted)]'}`}>All</span>
                {!selectedTab && <div className="w-6 h-0.5 bg-[var(--theme-primary)] rounded-full" />}
              </button>
              {categories.slice(0, 6).map((cat, i) => {
                const Icon = catIcons[(i + 1) % catIcons.length];
                const active = selectedTab === cat.id;
                return (
                  <button key={cat.id} onClick={() => handleSelectTab(cat.id)}
                    className="flex flex-col items-center gap-1 shrink-0 min-w-[56px]"
                    data-testid={`zepto-tab-${cat.id}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center overflow-hidden ${active ? 'bg-[var(--theme-primary)] text-white' : 'bg-[var(--theme-secondary)] text-[var(--theme-primary)]'}`}>
                      {cat.image_url ? (
                        <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                      ) : (
                        <Icon className="w-5 h-5" />
                      )}
                    </div>
                    <span className={`text-[10px] font-medium text-center line-clamp-1 ${active ? 'text-[var(--theme-primary)] font-bold' : 'text-[var(--theme-text-muted)]'}`}>{cat.name}</span>
                    {active && <div className="w-6 h-0.5 bg-[var(--theme-primary)] rounded-full" />}
                  </button>
                );
              })}
            </div>

            {/* When category selected: show filtered grid */}
            {selectedTab ? (
              <div className="px-4 pb-6" data-testid="zepto-category-products">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)]">
                    {categories.find(c => c.id === selectedTab)?.name}
                  </h3>
                  <span className="text-xs text-[var(--theme-text-muted)]">{filteredProducts.length} items</span>
                </div>
                <SubCategoryFilter
                  categories={categories}
                  selectedTab={selectedTab}
                  selectedSubCategories={selectedSubCategories}
                  onSelect={setSelectedSubCategories}
                />
                {loading ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{[1,2,3,4].map(i => <div key={i} className="h-52 bg-purple-50 rounded-xl animate-pulse" />)}</div>
                ) : filteredProducts.length === 0 ? (
                  <p className="text-center py-8 text-[var(--theme-text-muted)] text-sm">No products here yet</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {filteredProducts.map(p => (
                      <ZeptoProductGrid key={p.id} product={p} curr={currencySymbol}
                        getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                        onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                {/* ===== BIG SALE BANNER ===== (admin: Settings → Home Page Banner) */}
                {saleBanner && (
                <div className="px-4 mb-5" data-testid="zepto-sale-banner">
                  <div className="rounded-2xl overflow-hidden relative bg-gradient-to-br from-[#5B2EBF] via-[#7C3AED] to-[#A855F7] p-5 text-white shadow-lg" style={{ minHeight: '110px' }}>
                    <div className="relative z-10">
                      <h2 className="font-heading font-black text-2xl tracking-tight leading-tight break-words">{saleBanner.title}</h2>
                      {saleBanner.subtitle && <p className="text-sm mt-1 opacity-90 font-medium break-words">{saleBanner.subtitle}</p>}
                      {saleBanner.badge && (
                      <div className="flex items-center gap-2 mt-2">
                        <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-semibold">{saleBanner.badge}</span>
                      </div>
                      )}
                    </div>
                    {/* Decorative circles */}
                    <div className="absolute right-0 top-0 w-32 h-full opacity-20">
                      <div className="absolute right-4 top-4 w-16 h-16 rounded-full bg-white/30" />
                      <div className="absolute right-0 bottom-0 w-24 h-24 rounded-full bg-white/20" />
                    </div>
                  </div>
                </div>
                )}

                {/* Admin Promo Banners */}
                <PromoBannerCarousel banners={promoBanners} variant="zepto" />

                {/* Coupons */}
                {/* Promotions (automatic discounts) */}
                <PromotionStrip promotions={promotions} variant="zepto" />

                <CouponStrip coupons={coupons} variant="zepto" currencySymbol={currencySymbol} />

                {/* ===== SHOP BY CATEGORY - 2 col grid ===== */}
                {categories.length > 0 && (
                  <div className="px-4 mb-5" data-testid="zepto-shop-by-category">
                    <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)] mb-3">
                      Shop by Category
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                      {categories.slice(0, 8).map((cat, i) => {
                        const Icon = catIcons[(i) % catIcons.length];
                        return (
                          <button key={cat.id} onClick={() => handleSelectTab(cat.id)}
                            className="zepto-cat-card" data-testid={`zepto-catgrid-${cat.id}`}>
                            <span className="text-xs font-bold text-[var(--theme-primary)]">{cat.name}</span>
                            <div className="w-full h-16 rounded-lg bg-[var(--theme-secondary)] flex items-center justify-center overflow-hidden">
                              {cat.image_url ? (
                                <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                              ) : (
                                <Icon className="w-8 h-8 text-[var(--theme-primary)] opacity-40" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ===== ULTRA SAVING DEALS ===== */}
                {dealProducts.length > 0 && (
                  <div className="mb-5" data-testid="zepto-deals-section">
                    <div className="bg-gradient-to-r from-[var(--theme-secondary)] to-[#F5F0FF] rounded-t-2xl mx-4 px-4 pt-3 pb-2">
                      <h3 className="font-heading font-black text-base text-[var(--theme-primary)] text-center">
                        Ultra Saving Deals
                      </h3>
                    </div>
                    <div className="flex gap-3 overflow-x-auto scrollbar-hide px-4 pb-4 pt-3 snap-x" data-testid="zepto-deals-scroll">
                      {dealProducts.map(p => (
                        <ZeptoProductCarousel key={p.id} product={p} curr={currencySymbol}
                          getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                      ))}
                    </div>
                  </div>
                )}

                {/* ===== OFFERS STRIP ===== */}
                <div className="px-4 mb-5" data-testid="zepto-offers-strip">
                  <div className="bg-gray-900 text-white rounded-xl px-4 py-3 flex items-center gap-3">
                    <Gift className="w-5 h-5 text-purple-300 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-bold">Get a free gift for {currencySymbol}1</p>
                      <p className="text-xs text-gray-400">Shop for {currencySymbol}1199 more</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>

                {/* ===== ALL PRODUCTS ===== */}
                <div className="px-4 pb-6" data-testid="zepto-all-products">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading font-bold text-base text-[var(--theme-text-main)]">All Products</h3>
                    <Link to="/products" className="text-xs text-[var(--theme-primary)] font-medium flex items-center gap-0.5">
                      See all <ChevronRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                  {loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">{[1,2,3,4].map(i => <div key={i} className="h-52 bg-purple-50 rounded-xl animate-pulse" />)}</div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                      {allProducts.map(p => (
                        <ZeptoProductGrid key={p.id} product={p} curr={currencySymbol}
                          getItemQuantity={getItemQuantity} onAdd={(v) => handleAdd(p, v)} onAddBase={() => handleAdd(p)}
                          onUpdate={(cartKey, qty) => updateQuantity(cartKey, qty)} storeSettings={storeSettings} autoPlay={autoPlay} />
                      ))}
                    </div>
                  )}
                  {products.length > 20 && (
                    <Link to="/products" className="block mt-4" data-testid="zepto-view-all-btn">
                      <Button className="w-full bg-white text-[var(--theme-primary)] border border-[var(--theme-primary)] hover:bg-[var(--theme-secondary)] rounded-xl">
                        View All Products
                      </Button>
                    </Link>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Reserves scroll space for the floating cart bar below (which is `fixed` and
          so doesn't occupy flow height on its own) — without this the last product
          card ends up partially hidden behind it. */}
      {totalItems > 0 && <div className="h-16" aria-hidden="true" />}

      {/* ===== FLOATING CART ===== */}
      {totalItems > 0 && (
        <div className="fixed left-0 right-0 z-40 px-4 pb-2" style={{ bottom: 'calc(68px + env(safe-area-inset-bottom, 0px))' }} data-testid="zepto-floating-cart">
          <div className="max-w-3xl mx-auto">
            <Link to="/cart" className="block">
              <div className="bg-[var(--theme-primary)] text-white rounded-xl px-4 py-3 flex items-center justify-between shadow-lg">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  <span className="font-semibold text-sm">{totalItems} item{totalItems > 1 ? 's' : ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{currencySymbol}{totalAmount.toFixed(0)}</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      )}

      {/* ===== BOTTOM NAV ===== */}
      <ZeptoBottomNav />
    </div>
  );
};

/* ==================================== */
/*  Product Card - Carousel (Zepto)     */
/* ==================================== */
const ZeptoProductCarousel = ({ product, curr, getItemQuantity, onAdd, onAddBase, onUpdate, storeSettings, autoPlay }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);
  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const saveAmount = displayMrp > displayPrice ? Math.round(displayMrp - displayPrice) : 0;
  const qty = hasVariants ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0) : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="zepto-product-card" data-testid={`zepto-carousel-${product.id}`}>
      {discount > 0 && <div className="zepto-discount-badge">{discount}% OFF</div>}
      <div className="w-full aspect-square bg-gray-50 relative flex items-center justify-center overflow-hidden">
        <ProductImageCarousel product={product} autoPlay={autoPlay} />
        {isUnavailable(product) && !comingSoon && <UnavailableBadge />}
        {comingSoon ? <ComingSoonButton size="xs" className="absolute bottom-2 right-2 z-10" testId={`coming-soon-c-${product.id}`} /> : !productOos && (qty === 0 ? (
          <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()} className="zepto-add-btn" data-testid={`zepto-add-c-${product.id}`}>
            <Plus className="w-4 h-4" />
          </button>
        ) : (
          <div className="zepto-qty-inline">
            <button onClick={() => onUpdate(cartKey, qty - 1)} data-testid={`zepto-minus-c-${product.id}`}><Minus className="w-3 h-3" /></button>
            <span>{qty}</span>
            <button onClick={() => onUpdate(cartKey, qty + 1)} data-testid={`zepto-plus-c-${product.id}`}><Plus className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <div className="p-2.5 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="zepto-price-badge">{curr}{displayPrice}</span>
          {discount > 0 && <span className="text-xs text-[var(--theme-text-muted)] line-through">{curr}{displayMrp}</span>}
        </div>
        {saveAmount > 0 && <p className="text-[10px] font-semibold text-green-600 mb-1">{curr}{saveAmount} OFF</p>}
        <h4 className="text-xs font-semibold text-[var(--theme-text-main)] line-clamp-2 leading-tight">{product.name}</h4>
        {product.description && <p className="text-[10px] text-[var(--theme-text-muted)] line-clamp-1 mt-0.5">{product.description}</p>}
        <div className="flex items-center gap-1 mt-auto pt-1">
          <p className="text-[10px] text-[var(--theme-text-muted)]">{activeVariant ? activeVariant.label : product.unit}</p>
          {hasVariants && !productOos && !comingSoon && (
            <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
              className="flex items-center text-[9px] font-semibold text-[var(--theme-primary)]"
              data-testid={`change-variant-${product.id}`}>
              Change <ChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
          variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
          currencySymbol={curr} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
      </div>
    </div>
  );
};

/* ==================================== */
/*  Product Card - Grid (Zepto)         */
/* ==================================== */
const ZeptoProductGrid = ({ product, curr, getItemQuantity, onAdd, onAddBase, onUpdate, storeSettings, autoPlay }) => {
  const hasVariants = product.variants && product.variants.length > 0;
  const defaultVariant = hasVariants ? (product.variants.find(v => v.is_default) || product.variants[0]) : null;
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [pickerOpen, setPickerOpen] = useState(false);
  const productOos = isUnavailable(product, storeSettings);
  const comingSoon = isComingSoon(product, storeSettings);
  const activeVariant = selectedVariant || defaultVariant;
  const displayPrice = activeVariant ? activeVariant.price : product.price;
  const displayMrp = activeVariant ? activeVariant.mrp : product.mrp;
  const discount = displayMrp > displayPrice ? Math.round((1 - displayPrice / displayMrp) * 100) : 0;
  const saveAmount = displayMrp > displayPrice ? Math.round(displayMrp - displayPrice) : 0;
  const qty = hasVariants ? (activeVariant ? getItemQuantity(product.id, activeVariant.label) : 0) : getItemQuantity(product.id);
  const cartKey = hasVariants && activeVariant ? `${product.id}__${activeVariant.label}` : product.id;

  return (
    <div className="bg-white rounded-xl border border-[var(--theme-border)] overflow-hidden relative" data-testid={`zepto-grid-${product.id}`}>
      {discount > 0 && <div className="zepto-discount-badge">{discount}% OFF</div>}
      <div className="aspect-square bg-gray-50 relative flex items-center justify-center overflow-hidden">
        <ProductImageCarousel product={product} autoPlay={autoPlay} />
        {isUnavailable(product) && !comingSoon && <UnavailableBadge />}
        {comingSoon ? <ComingSoonButton size="xs" className="absolute bottom-2 right-2 z-10" testId={`coming-soon-g-${product.id}`} /> : !productOos && (qty === 0 ? (
          <button onClick={() => hasVariants ? onAdd(activeVariant) : onAddBase?.()} className="zepto-add-btn" data-testid={`zepto-add-g-${product.id}`}>
            <Plus className="w-4 h-4" />
          </button>
        ) : (
          <div className="zepto-qty-inline">
            <button onClick={() => onUpdate(cartKey, qty - 1)} data-testid={`zepto-minus-g-${product.id}`}><Minus className="w-3 h-3" /></button>
            <span>{qty}</span>
            <button onClick={() => onUpdate(cartKey, qty + 1)} data-testid={`zepto-plus-g-${product.id}`}><Plus className="w-3 h-3" /></button>
          </div>
        ))}
      </div>
      <div className="p-2.5">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="zepto-price-badge">{curr}{displayPrice}</span>
          {discount > 0 && <span className="text-xs text-[var(--theme-text-muted)] line-through">{curr}{displayMrp}</span>}
        </div>
        {saveAmount > 0 && <p className="text-[10px] font-semibold text-green-600 mb-1">{curr}{saveAmount} OFF</p>}
        <h4 className="text-xs font-semibold text-[var(--theme-text-main)] line-clamp-2 leading-tight">{product.name}</h4>
        {product.description && <p className="text-[10px] text-[var(--theme-text-muted)] line-clamp-1 mt-0.5">{product.description}</p>}
        <div className="flex items-center gap-1 mt-1">
          <p className="text-[10px] text-[var(--theme-text-muted)]">{activeVariant ? activeVariant.label : product.unit}</p>
          {hasVariants && !productOos && !comingSoon && (
            <button onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}
              className="flex items-center text-[9px] font-semibold text-[var(--theme-primary)]"
              data-testid={`change-variant-${product.id}`}>
              Change <ChevronDown className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
        <VariantPicker open={pickerOpen} onOpenChange={setPickerOpen} productId={product.id} productName={product.name}
          variants={product.variants} selectedVariant={activeVariant} onSelect={setSelectedVariant}
          currencySymbol={curr} getItemQuantity={getItemQuantity} storeSettings={storeSettings} />
      </div>
    </div>
  );
};

/* ==================================== */
/*  Bottom Nav (Zepto)                  */
/* ==================================== */
const ZeptoBottomNav = () => {
  const loc = typeof window !== 'undefined' ? window.location.pathname : '/';
  const { totalItems } = useCart();
  const { user, storeSettings } = useAuth();
  const isSubsEnabled = storeSettings?.tenant_features?.subscriptions === true;
  const isGuest = !user;

  const items = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/products', icon: Grid3X3, label: 'Categories' },
    ...(isGuest ? [] : [{ path: '/orders', icon: Package, label: 'Orders' }]),
    ...(!isGuest && isSubsEnabled ? [{ path: '/subscriptions', icon: CalendarCheck, label: 'Subscribe' }] : []),
    ...(isGuest ? [{ path: '/login', icon: User, label: 'Login' }] : [{ path: '/profile', icon: User, label: 'Profile' }]),
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[var(--theme-border)]"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} data-testid="zepto-bottom-nav">
      <div className="max-w-3xl mx-auto flex items-center justify-around py-2">
        {items.map(item => {
          const active = item.path === '/' ? loc === '/' : loc.startsWith(item.path);
          return (
            <Link key={item.path} to={item.path}
              className={`flex flex-col items-center gap-0.5 px-3 py-1 relative transition-colors ${active ? 'text-[var(--theme-primary)]' : 'text-[var(--theme-text-muted)]'}`}
              data-testid={`zepto-bnav-${item.label.toLowerCase().replace(' ', '-')}`}>
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default ZeptoHome;
